import React from 'react'
import { Button, Space, Typography, theme, Row, Col, Flex } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import './styles.scss'
import CategoryCard from './CategoryCard'
import OtherCategorySelector from './OtherCategorySelector'
import BatchIndicator from './BatchIndicator'
import { useRefinementFlow } from '../hooks/useRefinementFlow'
import useRubricContext from '../../../../../../../hooks/useRubricContext'

const { Title } = Typography

const sortCategoriesByRubricOrder = (categories = [], rubricCategories = []) => {
    const safeCategories = Array.isArray(categories) ? categories : []
    const safeRubricCategories = Array.isArray(rubricCategories) ? rubricCategories : []
    const categoryOrderById = new Map(safeRubricCategories.map((category, index) => [category.id, index]))

    return [...safeCategories].sort((a, b) => {
        const aOrder = categoryOrderById.get(a.id)
        const bOrder = categoryOrderById.get(b.id)

        if (aOrder == null && bOrder == null) return 0
        if (aOrder == null) return 1
        if (bOrder == null) return -1

        return aOrder - bOrder
    })
}

const toCategoryCardData = category => ({
    id: category.id,
    description: category.description,
    explanation: category.explainability?.reasoning || '',
    isAdequate: category.explainability?.isAdequate,
    tags: category.tags,
})

/**
 * Component that renders a category choice message for a submission
 */
const CategoryChoiceMessage = ({ refinementItem }) => {
    const { isItemEditable, handleSkip } = useRefinementFlow()
    const { categories: rubricCategories } = useRubricContext()
    const isSkipped = Boolean(refinementItem?.isSkipped)
    const isDisabled = !isItemEditable(refinementItem?.id)
    const { token: themeToken } = theme.useToken()

    const onSkip = () => handleSkip(refinementItem.id)

    const displayedCategories = sortCategoriesByRubricOrder(
        refinementItem?.categories,
        rubricCategories,
    ).slice(0, 2)

    // Check if an "other" category (not one of the first two) has been selected
    const selectedCategoryId = refinementItem?.selectedCategoryId
    const displayedCategoryIds = displayedCategories.map(category => category.id)
    const hasChosenOtherCategory = selectedCategoryId && !displayedCategoryIds.includes(selectedCategoryId)

    // If the refinementItem doesn't have the required data, don't render
    if (displayedCategories.length < 2) {
        return null
    }

    return (
        <div className='category-choice-message' data-test='refinement-category-choice'>
            <Space direction='vertical' style={{ width: '100%' }}>
                <Flex justify='space-between' align='top'>
                    <Title
                        level={5}
                        className='category-choice__title'
                        style={{ color: themeToken.colorTextSecondary }}
                    >
                        For the submission displayed, I've identified two potentially applicable categories:
                    </Title>
                    <BatchIndicator refinementItemId={refinementItem.id} />
                </Flex>
                <Row gutter={16}>
                    <Col span={12}>
                        <CategoryCard
                            category={toCategoryCardData(displayedCategories[0])}
                            refinementItemId={refinementItem.id}
                            isDisabled={isDisabled}
                        />
                    </Col>
                    <Col span={12}>
                        <CategoryCard
                            category={toCategoryCardData(displayedCategories[1])}
                            refinementItemId={refinementItem.id}
                            isDisabled={isDisabled}
                        />
                    </Col>
                </Row>

                <Flex justify='space-between'>
                    <OtherCategorySelector
                        refinementItem={refinementItem}
                        isDisabled={isDisabled}
                        selectedCategoryId={selectedCategoryId}
                        displayedCategoryIds={displayedCategoryIds}
                        hasChosen={hasChosenOtherCategory}
                    />
                    <Button
                        onClick={onSkip}
                        type={isSkipped ? 'text' : 'text'}
                        disabled={isDisabled}
                        icon={!isSkipped && <QuestionCircleOutlined />}
                        className='category-choice__skip-button'
                        style={
                            isSkipped
                                ? {
                                      backgroundColor: themeToken.colorErrorBg,
                                      borderColor: themeToken.colorError,
                                      color: themeToken.colorError,
                                      fontWeight: 500,
                                  }
                                : {}
                        }
                    >
                        {isSkipped ? 'Skipped' : "I'm not sure"}
                    </Button>
                </Flex>
            </Space>
        </div>
    )
}

export default CategoryChoiceMessage
